import React, { useState } from 'react';
import { faMobileAlt } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

import { Subscription, useNotificationSetting, useShipmentData, useSubscriptionsData } from '../dataProviders';
import { makeBtStyles } from '../Theme';

import Icon from './Icon';
import { BtLink } from './BtLink';
import SmsSubscribeForm from './SmsSubscribeForm'
import SmsTermsDialog, { useDialog } from './SmsTermsDialog';
import Button from './Button';


const useStyles = makeBtStyles( theme => ({
  root: {
    position: 'relative',
    marginBottom: '-12px'
  },
  phoneIcon: {
    fontSize: '30px',
    position: 'absolute',
    top: '6px',
    left: '0'
  },
  button: {
    marginBottom: '12px'
  },
  formWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap'
  },
  subscribedMessage: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: '38px',
    '& p': {
      margin: '0 20px 12px 0',
      alignSelf: 'center'
    },
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap'
    }
  },
  mainMessage: {
    margin: '2px 12px 12px 38px'
  },
  form: {
    marginBottom: '12px'
  },
  termsMessage: {
    fontSize: '12px',
    margin: 0,
    color: '#929292'
  },
}));


enum SubscriptionState { Form = 1, Previously, JustNow };

export function useSmsControlsShown() {
  const { sms_enabled } = useNotificationSetting();
  const { destination } = useShipmentData();

  return sms_enabled && destination?.country_code === 'US';
}

export default function SmsControls() {
  const classes = useStyles();
  const showSms = useSmsControlsShown();
  const [openTerms, toggleTerms] = useDialog();
  const { firstActiveSubscription, updateSubscriptions } = useSubscriptionsData();
  const subscription = firstActiveSubscription();

  const initialState = subscription ? SubscriptionState.Previously : SubscriptionState.Form;
  const [subscriptionState,setSubscriptionState] = useState<SubscriptionState>(initialState);

  const onSubscribe = (newSubscriptions: Subscription[]) => {
    updateSubscriptions(newSubscriptions);
    setSubscriptionState(SubscriptionState.JustNow);
  };

  const onCancelUpdate = () => {
    setSubscriptionState(SubscriptionState.JustNow);
  };
  
  if( !showSms ) {
    return null;
  }

  return <div className={classes.root}>
    <Icon className={classes.phoneIcon} icon={faMobileAlt} />
    { (subscriptionState === SubscriptionState.JustNow) ? <div className={classes.subscribedMessage}>
      <FormattedMessage
        description="SmsControls.justSubscribed.message"
        defaultMessage="Thanks for signing up. Check your mobile number ending in {lastFourDigits} to confirm."
        values={{ lastFourDigits: lastFourDigits(subscription?.phone_number) }}
        tagName="p"
      />
      <Button onClick={() => setSubscriptionState(SubscriptionState.Form)} className={classes.button}>
        <FormattedMessage description="SmsControls.justSubscribed.button" defaultMessage="Update Number" />
      </Button>
    </div> : (subscriptionState === SubscriptionState.Previously) ? <div className={classes.subscribedMessage}>
      <FormattedMessage
        description="SmsControls.alreadySubscribed.message"
        defaultMessage="The mobile number ending in {lastFourDigits} is now receiving shipping and delivery updates via text."
        values={{ lastFourDigits: lastFourDigits(subscription?.phone_number) }}
        tagName="p"
      />
      <Button onClick={() => setSubscriptionState(SubscriptionState.Form)} className={classes.button}>
        <FormattedMessage description="SmsControls.alreadySubscribed.button" defaultMessage="Update Number" />
      </Button>
    </div> : <div className={classes.formWrapper}>
      <div className={classes.mainMessage}>
        <FormattedMessage
          description="SmsControls.mainMessage"
          defaultMessage="Receive shipping & delivery updates by text."
        />
        <p className={classes.termsMessage}>
          <FormattedMessage
            description="SmsControls.termsMessage"
            defaultMessage="By entering a number you accept the <link>terms</link>."
            values={{ link: (chunks: any) => <BtLink onClick={toggleTerms}>{chunks}</BtLink> }}
          />
        </p>
      </div>
      <SmsSubscribeForm
        onSuccess={onSubscribe}
        onCancel={onCancelUpdate}
        subscription={subscription}
        className={classes.form}
      />
    </div>}
    <SmsTermsDialog open={openTerms} toggle={toggleTerms} />
  </div>;
}

function lastFourDigits(number='') {
  return number.slice( Math.max(number.length-4,0), number.length );
}